import { Button, Loader } from "@mantine/core";
import { Dock, DockStatus } from "./types";

// Inline styles (not Tailwind): these views ship inside the robotics bundle,
// which does NOT include Tailwind — only Mantine (host-provided) + inline CSS.
const c = {
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate700: "#334155",
  slate800: "#1e293b",
  slate900: "#0f172a",
  border: "#e2e8f0",
  white: "#ffffff",
  green: "#10b981",
  greenText: "#059669",
  blue: "#3b82f6",
  blueText: "#2563eb",
  amber: "#f59e0b",
  amberText: "#b45309",
  amberBg: "#fffbeb",
  amberBorder: "#fcd34d",
  amberPillBg: "#fef3c7",
  amberPillText: "#92400e",
};

interface DockStatusViewProps {
  data?: DockStatus;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  /** Short location name shown in the header (from the location selector). */
  locationLabel?: string;
  /** When set, occupied docks show a Return Unit button that invokes this. */
  onReturnUnit?: (dock: Dock) => void;
  /** Id of the dock whose return is currently in flight (button shows loading). */
  returningDockId?: number | null;
}

export function DockStatusView({
  data,
  isLoading,
  isError,
  error,
  locationLabel,
  onReturnUnit,
  returningDockId,
}: DockStatusViewProps) {
  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#991b1b", marginBottom: 8 }}>Error Loading Docks</div>
        <div style={{ color: "#b91c1c" }}>
          {error instanceof Error ? error.message : "Something went wrong."}
        </div>
      </div>
    );
  }

  const { dwellThresholdMinutes, docks } = data;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c.slate400, marginBottom: 4 }}>
          Dock Status — {locationLabel ?? "—"}
        </div>
        <div style={{ color: c.slate500 }}>
          Real-time view of every dock at your location. A dock stays green until a unit waits
          too long — then it's flagged automatically.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 16 }}>
        {docks.map((dock) => (
          <DockCard
            key={dock.id}
            dock={dock}
            thresholdMinutes={dwellThresholdMinutes}
            onReturnUnit={onReturnUnit}
            isReturning={returningDockId === dock.id}
          />
        ))}
      </div>
    </div>
  );
}

function DockCard({
  dock,
  thresholdMinutes,
  onReturnUnit,
  isReturning,
}: {
  dock: Dock;
  thresholdMinutes: number;
  onReturnUnit?: (dock: Dock) => void;
  isReturning: boolean;
}) {
  const returning = dock.status === "Returning";
  const occupied = dock.status !== "Available";
  const longDwell = dock.isLongDwell;
  const canReturn = dock.status === "Occupied" && !!onReturnUnit && dock.currentUnitId != null;

  const dotColor = !occupied ? c.green : returning ? c.blue : longDwell ? c.amber : c.blue;
  const statusText = !occupied
    ? "Available"
    : returning
      ? "Returning to storage…"
      : longDwell
        ? "Occupied — long dwell"
        : "Occupied";
  const statusColor = !occupied ? c.greenText : returning ? c.blueText : longDwell ? c.amberText : c.blueText;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 150,
        padding: 16,
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        background: longDwell ? c.amberBg : c.white,
        border: `1px solid ${longDwell ? c.amberBorder : c.border}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: c.slate900 }}>{dock.name}</div>
        {occupied && dock.minutesOnDock !== null && (
          <div style={{ textAlign: "right", lineHeight: 1.15 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: c.slate700 }}>{dock.minutesOnDock}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.slate500 }}> min</span>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: c.slate400 }}>On Dock</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: statusColor }}>{statusText}</span>
      </div>

      {occupied && dock.unitNumber && (
        <div style={{ fontSize: 14, color: c.slate500, marginTop: 4 }}>Unit {dock.unitNumber}</div>
      )}

      <div style={{ marginTop: "auto", paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        {longDwell && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "fit-content", background: c.amberPillBg, color: c.amberPillText, fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 6 }}>
            ⚠ Dwell exceeds {thresholdMinutes} min threshold
          </span>
        )}
        {canReturn && (
          <Button size="xs" variant="light" color="primary" fullWidth loading={isReturning} onClick={() => onReturnUnit!(dock)}>
            Return Unit
          </Button>
        )}
      </div>
    </div>
  );
}
